import * as React from "react";
import { Combobox } from "@/components/ui";
import { CompleteAccount } from "@/interfaces/dtos/Account";
import { UsersService } from "@/services/AccountsService";
import { Page } from "@/services/bedrock/dtos/Page";
import { usePaginatedCombobox } from "@/utils/usePaginatedCombobox";

export interface CoachOption {
  id: number;
  label: string;
}

const PAGE_SIZE = 25;

export function coachOptionFor(account: CompleteAccount): CoachOption | null {
  const id = Number(account.coach?.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  const name =
    account.coach?.companyName ||
    [account.profile?.firstName, account.profile?.lastName].filter(Boolean).join(" ") ||
    account.user.username ||
    "";
  return { id, label: account.user.email ? `${name} - ${account.user.email}` : name };
}

export async function searchCoachOptions(search: string | undefined, page: number): Promise<Page<CoachOption>> {
  const result = await UsersService.searchCoaches({ searchTerm: search ?? "" }, page, PAGE_SIZE);
  const content = (result.content ?? []).map(coachOptionFor).filter((o): o is CoachOption => o !== null);
  return {
    content,
    pagination: {
      page,
      perPage: PAGE_SIZE,
      totalPages: result.totalPages,
      totalCount: result.totalElements,
      hasMore: result.last === undefined ? page + 1 < (result.totalPages ?? 0) : !result.last,
    },
  };
}

export interface CoachPickerProps {
  coach: CoachOption | null;
  onCoachChange(coach: CoachOption | null): void;
}

export const CoachPicker: React.FC<CoachPickerProps> = ({ coach, onCoachChange }) => {
  // Seeded with the remembered coach so the name shows when the list remounts.
  const combobox = usePaginatedCombobox(searchCoachOptions, false, coach);
  const { onValueChange } = combobox;

  return (
    <Combobox
      {...combobox}
      value={coach}
      onValueChange={(option: CoachOption | null | undefined, details: unknown) => {
        onValueChange(option ?? null, details);
        onCoachChange(option ?? null);
      }}
      placeholder="Pick a coach…"
      className="min-w-64 max-w-80"
      showClear
    />
  );
};
